import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { SubprojectQuery } from 'src/app/akita/subproject/state/subprojects.query';
import { IntellegenceService } from 'src/app/core/service/api/intellegence.service';
import { Globals } from 'src/app/globals';
import { IqAudioNotesSummaryComponent } from './iq-audio-notes-summary/iq-audio-notes-summary.component';
import { LogsService } from 'src/app/core/service/api/logs.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppInjector } from 'src/app-injector';
import { UndoSnackbarComponent } from '../../../material/undo-snackbar/undo-snackbar.component';
import { GenericInfoModalComponent } from '../../../modal/generic-info-modal/generic-info-modal.component';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { MatDialog } from '@angular/material/dialog';
import { ContextService } from 'src/app/core/service/api/context.service';

@Component({
  selector: 'app-iq-generate-audio-notes-modal',
  templateUrl: './iq-generate-audio-notes-modal.component.html',
  styleUrl: './iq-generate-audio-notes-modal.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IqGenerateAudioNotesModalComponent {
  isDragging = false;
  selectedFile : File = null;
  fileSizeExceeded = false;
  private readonly MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
  allowedExtensions = ['flac', 'mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'ogg', 'wav', 'webm'];
  showGenerateProcess: boolean = false;
  summaryGenerated: boolean = false;
  meetingDate: any;
  processes: any[] = [{id: 1, name: translate('iq.analysis'), finished: false, inProgress: false, description: translate('iq.analyzing_audio') }, {id: 2, name: translate('iq.summarize'), finished: false, inProgress: false, description: translate('iq.summarising_all_topics') }, {id: 3, name: translate('iq.determine_topic'), finished: false, inProgress: false, description: translate('iq.determining_topic_contributions_per_speaker') }, {id: 4, name: translate('iq.determine_talks'), finished: false, inProgress: false, description: translate('iq.determining_tasks_discussed') }];
  meeting: any;
  showAction: boolean =  false;
  isProcessing: boolean = false;
  isLoading = signal<boolean>(false);
  @ViewChild(IqAudioNotesSummaryComponent) logSummary!: IqAudioNotesSummaryComponent;

  
  constructor(public globals: Globals, private dialogRef: DialogRef<IqGenerateAudioNotesModalComponent>, private dialog: MatDialog, private cdRef: ChangeDetectorRef, private subProjectQuery: SubprojectQuery, private _intelligenceService: IntellegenceService, private router: Router, private logsService: LogsService, private _contextService: ContextService) { }

  close(){
    this.dialogRef.close();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];
      if (this.isValidFileType(file)) {
        if (file.size > this.MAX_FILE_SIZE) {
          this.selectedFile = null;
          this.fileSizeExceeded = true;
        } else {
          this.selectedFile = file;
          this.fileSizeExceeded = false;
        }
        this.cdRef.markForCheck();
      }
    }
  }

  isValidFileType(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return this.allowedExtensions.includes(extension);
  }

  onFileUnSelect(){
    this.selectedFile = null;
    this.fileSizeExceeded = false;
    this.cdRef.markForCheck();
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    if (file && this.isValidFileType(file)) {
      if (file.size > this.MAX_FILE_SIZE) {
        this.selectedFile = null;
        this.fileSizeExceeded = true;
      } else {
        this.selectedFile = file;
        this.fileSizeExceeded = false;
      }
      this.cdRef.markForCheck();
    }
    event.target.value = null;
  }

  uploadFileForSummary(){
    let setDefaultHeader;
    const sessionToken = localStorage.getItem('session_token');
    if(this.router.url.includes('sub')){
      const activeSubProject = this.subProjectQuery.getActive();
      setDefaultHeader={
        'X-Project': this.globals.projectCode,
        'X-Subproject': activeSubProject.id.toString(),
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
      if (sessionToken) {
        setDefaultHeader['X-Session-Token'] = sessionToken;
      }
    }else{
      setDefaultHeader={
        'X-Project': this.globals.projectCode,
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
      if (sessionToken) {
        setDefaultHeader['X-Session-Token'] = sessionToken;
      }
    }
    if (this.selectedFile && this.isValidFileType(this.selectedFile) && this.meetingDate) {
      this._intelligenceService.uploadMeetingFile(this.selectedFile, this.meetingDate, setDefaultHeader).subscribe({
        next:(res)=>{
          this.summaryGenerated = true;
          this.meeting = res.metadata.notes.meeting;
          this.meeting = this.mergeTasksIntoTopics(this.meeting);
          this.meeting = {...this.meeting, meetingDate: this.meetingDate}
          this.cdRef.markForCheck();
        },
        error: (err)=> { 
          this.close(); 
          this.cdRef.markForCheck();
        }
      })
    }
  }


  mergeTasksIntoTopics(meeting: any) {
    if (!meeting?.topics) return meeting;
  
    const tasks = meeting?.tasks || [];
    const suggestedTasks = meeting?.follow_up_advice || [];
  
    meeting.topics = meeting.topics.map((topic: any, index: number) => {
      const tasksForTopic = tasks.filter(
        (task: any) => task.topic_index === index
      );

      const suggestedTasksForTopic = suggestedTasks.filter(
        (task: any) => task.topic_index === index
      );

      return {
        ...topic,
        tasks: tasksForTopic.length ? tasksForTopic : [],   // ensure array
        suggestedTasks: suggestedTasksForTopic.length ? suggestedTasksForTopic : [] // ensure array
      };
    });
    
    return meeting;
  }

  checkAiCreditsAvailable() {
    this.isLoading.set(true);
    this._intelligenceService.checkAiCredits().subscribe({
      next: (res: any) => {
        if (res.code==="CREDITS_EXISTS") {
          this.showGenerateProcess = true; 
          this.runProcessesSequentially()
        } else {
          this.close();
          const month = this._contextService.getNextMonthName();
          const body = translate('contexts.ai_credit_limit_info', { month }).replace(/\n/g, '<br>');
          
          this.dialog.open(GenericInfoModalComponent, modalConfig(
            {
              data: {
                title: translate('contexts.ai_credit_limit_reached'),
                body
              },
              disableClose: false,
              panelClass: 'customized-dialog-container'
            },
            ModalEnum.ModalSmall
          ))
        }
      },
      error: (err) => {
        this.isLoading.set(false);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    })
  }

  runProcessesSequentially() {
    let timeout = 1500;

    if (this.selectedFile && this.isValidFileType(this.selectedFile) && this.meetingDate) {
      // Adjust timeout based on file size. The bigger the file, the longer the timeout.
      // Minimum timeout is 1.5 seconds.
      // If upload is done earlier, the timeout is adjusted to the remaining time.
      timeout = Math.max(1500, this.selectedFile.size * 0.001);
    }

    this.uploadFileForSummary();
    let index = 0;
    const processNext = () => {
      if (index >= this.processes.length) {
        this.cdRef.markForCheck();
        return
      };
      this.processes[index].inProgress = true;
      this.processes[index].finished = false;
      this.cdRef.markForCheck();
      setTimeout(() => {
        if (this.summaryGenerated) {
          timeout = 200;
        }

        if (index !== this.processes.length - 1) {
          this.processes[index].inProgress = false;
          this.processes[index].finished = true;
          this.cdRef.markForCheck();
          index++;
          processNext();
        }
      }, timeout);
    };
    processNext();
  }

  generateContactMoment() {
    this.isProcessing = true
    this.logSummary.getContent().subscribe({
      next: log => {
        this.logsService.submitLog(log).subscribe({
          next: (log: any) => {
            this.isProcessing = false;

            const snackbar: MatSnackBar = AppInjector ? AppInjector.get(MatSnackBar) : null;
            snackbar.openFromComponent(UndoSnackbarComponent, {
                data: {
                  data:log,
                  message: translate('error.stored'),
                  type:'openItems',
                  item:'contactmomenten',
                  url:'stakeholders/contactmomenten'
                },
                duration: 4000,
                horizontalPosition: 'start',
                panelClass: 'snackbar-background-green',
            });
            this.dialogRef.close();
            this.cdRef.markForCheck();
          },
          error: (err) => {
            this.isProcessing = false;
            this.cdRef.markForCheck();
          }
        })
      }
    });
  }

  updateMeeting(meeting: any) {
    this.meeting = meeting;
  }

  hasEmptyDeadline(): boolean {
    if (!this.meeting?.topics) return true;
  
    // Check normal tasks
    const hasEmptyTaskDeadline = this.meeting.topics.some(topic =>
      topic.tasks?.some(task => !task.deadline)
    );
  
    return hasEmptyTaskDeadline;
  }

  allowAction(action: boolean) {
    this.showAction = action
  }
}
